import { forwardRef, useEffect, useImperativeHandle, useState, useCallback, useMemo } from "react"
import { Form, Input } from "antd"
import { H4 } from "components/ui/typography/titles/Titles"
import { OrderedModelNumbersSelect } from "components/ui/orderedModelNumbersSelect/OrderedModelNumbersSelect"
import { ModelNumberOrderInfo } from "components/ui/modelNumberOrderInfo/ModelNumberOrderInfo"
import { Select } from "components/ui/select/Select"
import { useCategories } from "hooks/categories.hook"
import { useAddDualPartCategoryStore } from "store/dualPartCategory.store"
import { AddModelObjectProvider } from "components/providers/Forms.provider"
import { useWithoutCategoryModelNumbers } from "hooks/withoutCategoryModelNumbers.hook"
import { OBJECT_LABEL_VALIDATION, OBJECT_VALIDATION_MESSAGES } from "helpers/validation/objectValidation.helper"
import type { IAddObjectMethods } from "types/configuration.type"
import type { TTopLevelCategory } from "types/drawer.type"
import style from "./DualPartObject.module.scss"

const { addModelObjectForm, block, label, description, endOfSection, title, select, startOfSection } = style

type Props = {
	objectCategoriesData: TTopLevelCategory[]
}

export const DualPartObject = forwardRef<IAddObjectMethods, Props>(({ objectCategoriesData }: Props, ref) => {
	const [form] = Form.useForm()
	const values = Form.useWatch([], form)

	const [selectedTopVevelCategory, setSelectedTopLevelCategory] = useState<string | number | undefined>()

	const { topLevelCategories, superCategories } = useCategories(objectCategoriesData, selectedTopVevelCategory)
	const {
		setFormValues,
		category,
		setLeftModelNumbers,
		setRightModelNumbers,
		setWholeModelNumbers,
		resetForm: resetStoreForm,
	} = useAddDualPartCategoryStore()
	const { options: modelNumberOptions } = useWithoutCategoryModelNumbers()

	const leftOptions = useMemo(
		() => modelNumberOptions.filter((opt) => !category.rightModelNumbers.includes(opt.value) && !category.wholeModelNumbers.includes(opt.value)),
		[modelNumberOptions, category.rightModelNumbers, category.wholeModelNumbers],
	)

	const rightOptions = useMemo(
		() => modelNumberOptions.filter((opt) => !category.leftModelNumbers.includes(opt.value) && !category.wholeModelNumbers.includes(opt.value)),
		[modelNumberOptions, category.leftModelNumbers, category.wholeModelNumbers],
	)

	const wholeOptions = useMemo(
		() => modelNumberOptions.filter((opt) => !category.leftModelNumbers.includes(opt.value) && !category.rightModelNumbers.includes(opt.value)),
		[modelNumberOptions, category.leftModelNumbers, category.rightModelNumbers],
	)

	// Set form values
	useEffect(() => {
		setFormValues({
			name: values?.name,
			label: values?.label,
			topLevelCategory: values?.topLevelCategory,
			superCategory: values?.superCategory,
		})
	}, [values, setFormValues])

	// Change top level category
	const onChangeTopLevelCategoryHandle = (value: string | number) => {
		setSelectedTopLevelCategory(value)
		form.resetFields(["superCategory"])
	}

	const onLeftModelNumbersChange = useCallback(
		(selectedModelNumbers: number[]) => {
			setLeftModelNumbers(selectedModelNumbers)
		},
		[setLeftModelNumbers],
	)

	const onRightModelNumbersChange = useCallback(
		(selectedModelNumbers: number[]) => {
			setRightModelNumbers(selectedModelNumbers)
		},
		[setRightModelNumbers],
	)

	const onWholeModelNumbersChange = useCallback(
		(selectedModelNumbers: number[]) => {
			setWholeModelNumbers(selectedModelNumbers)
		},
		[setWholeModelNumbers],
	)

	useImperativeHandle(ref, () => ({
		resetForm: () => {
			form.resetFields()
			resetStoreForm()
		},
	}))

	return (
		<AddModelObjectProvider>
			<Form form={form} size="large">
				<div className={addModelObjectForm}>
					<div className={block}>
						<span className={label}>Name</span>
						<span className={description}>Without [r], [l], or [w]</span>
						<Form.Item name="name" rules={[{ required: true, message: OBJECT_VALIDATION_MESSAGES.NAME_REQUIRED }]}>
							<Input data-testid="dualPartName" />
						</Form.Item>
					</div>
					<div className={`${block} ${endOfSection}`}>
						<span className={label}>Label</span>
						<span className={description}>
							<span className={description}>Used in train set, without [r], [l], or [w]</span>
							<Form.Item
								name="label"
								rules={[
									{
										required: true,
										message: OBJECT_VALIDATION_MESSAGES.LABEL_REQUIRED,
									},
									OBJECT_LABEL_VALIDATION.LABEL_PREFIX,
								]}
							>
								<Input data-testid="dualPartLabel" />
							</Form.Item>
						</span>
					</div>
					<div className={block}>
						<H4 className={title}>Categories</H4>
						<span className={label}>Top level category</span>
						<Form.Item
							name="topLevelCategory"
							rules={[{ required: true, message: OBJECT_VALIDATION_MESSAGES.TOP_LEVEL_CATEGORY_REQUIRED }]}
						>
							<Select
								placeholder="Select category"
								allowClear={false}
								variant="outlined"
								options={topLevelCategories}
								onChange={onChangeTopLevelCategoryHandle}
								className={select}
							/>
						</Form.Item>
					</div>
					<div className={block}>
						<span className={label}>Super category</span>
						<Form.Item
							name="superCategory"
							rules={[{ required: true, message: OBJECT_VALIDATION_MESSAGES.SUPER_CATEGORY_REQUIRED }]}
						>
							<Select
								placeholder="Select category"
								variant="outlined"
								allowClear={false}
								className={select}
								options={superCategories}
							/>
						</Form.Item>
					</div>
					<div className={`${block} ${startOfSection}`} data-testid="leftPartModelNumbers">
						<ModelNumberOrderInfo />
						<span className={label}>Left part</span>
						<OrderedModelNumbersSelect
							value={category.leftModelNumbers}
							onChange={onLeftModelNumbersChange}
							options={leftOptions}
							data-testid="left-model-numbers-select"
						/>
					</div>
					<div className={block} data-testid="rightPartModelNumbers">
						<span className={label}>Right part</span>
						<OrderedModelNumbersSelect
							value={category.rightModelNumbers}
							onChange={onRightModelNumbersChange}
							options={rightOptions}
							data-testid="right-model-numbers-select"
						/>
					</div>
					<div className={block} data-testid="wholeObjectModelNumbers">
						<span className={label}>Whole object</span>
						<OrderedModelNumbersSelect
							value={category.wholeModelNumbers}
							onChange={onWholeModelNumbersChange}
							options={wholeOptions}
							data-testid="whole-model-numbers-select"
						/>
					</div>
				</div>
			</Form>
		</AddModelObjectProvider>
	)
})
